import React from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { resyncPayment } from '@src/Api';
import type { PaymentData } from './types';

export const PaymentTab: React.FC<{ payments: PaymentData[]; loading: boolean; onResync: () => Promise<void> }> = ({ payments, loading, onResync }) => {
  const pending = React.useRef(new Set<number>());
  const [activeIds, setActiveIds] = React.useState<number[]>([]);
  const [feedback, setFeedback] = React.useState<{ success: boolean; message: string } | null>(null);
  const refresh = React.useRef(onResync);
  refresh.current = onResync;

  const retry = async (payment: PaymentData) => {
    if (pending.current.has(payment.id)) return;
    pending.current.add(payment.id);
    setActiveIds(Array.from(pending.current));
    setFeedback(null);
    try {
      const response = await resyncPayment(payment.id);
      const success = response?.status === 200;
      setFeedback({
        success,
        message: `${payment.payment_document_number}: ${
          success
            ? 'Payment synchronized successfully.'
            : typeof response?.data?.error === 'string'
            ? response.data.error
            : 'Payment synchronization failed. Please try again.'
        }`,
      });
    } catch {
      setFeedback({ success: false, message: 'Payment synchronization failed. Please try again.' });
    } finally {
      try {
        await refresh.current();
      } finally {
        pending.current.delete(payment.id);
        setActiveIds(Array.from(pending.current));
      }
    }
  };

  return (
    <>
      {feedback && <Alert variant={feedback.success ? 'success' : 'danger'}>{feedback.message}</Alert>}
      <Table striped responsive className="enhanced-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Payment document</th>
            <th>BC invoice</th>
            <th>Rentman invoice ID</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Rentman payment ID</th>
            <th>Error</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={9} className="table-loader">
                <Spinner className="data-loading-spinner" animation="border" role="status">
                  <span className="visually-hidden">Loading payments…</span>
                </Spinner>
              </td>
            </tr>
          ) : payments.length === 0 ? (
            <tr>
              <td colSpan={9}>No payment synchronization attempts yet.</td>
            </tr>
          ) : (
            payments.map((payment) => (
              <tr key={payment.id}>
                <td>{new Date(payment.created_at).toLocaleString('da-DK')}</td>
                <td>{payment.payment_document_number}</td>
                <td>{payment.bc_invoice_number}</td>
                <td>{payment.rentman_invoice_id}</td>
                <td>{Number(payment.amount).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                <td>{{ processing: 'Processing', completed: 'Completed', failed: 'Failed' }[payment.status]}</td>
                <td>{payment.rentman_payment_id ?? '—'}</td>
                <td>{payment.error_message || '—'}</td>
                <td>
                  {payment.status === 'failed' ? (
                    <Button disabled={activeIds.includes(payment.id)} onClick={() => void retry(payment)}>
                      {activeIds.includes(payment.id) ? 'Syncing…' : 'Re-sync'}
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  );
};
